import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";

import { Button } from "components/ui/Button";
import { Dialog } from "components/ui/Dialog/Dialog";
import { Textarea } from "components/ui/Textarea";
import { useCloseLabOrderTest } from "features/labOrders/hooks/useCloseLabOrderTest";
import {
  getLabTestLabel,
  isAlreadySettledError,
} from "features/labOrders/utils/outstandingLabTests";

import s from "./CloseLabTestDialog.module.css";

// The backend's own limit (CloseLabOrderTestRequest), enforced here so an over-long
// reason is stopped while it is being typed rather than by a 400 after the fact.
const REASON_MAX_LENGTH = 500;

/**
 * "This won't come back" (KIN-2087): a GP closes a waiting test whose result is never
 * coming — the sample was rejected, the test was cancelled, or it was sent elsewhere.
 *
 * The reason is required by the backend and is the only record of why the test left the
 * worklist, so the dialog asks for it rather than offering a fixed list of three causes:
 * what happened to a sample is a sentence, and the three the ticket names are a prompt
 * for it, not an enumeration of it.
 */
export const CloseLabTestDialog = ({ test, onClose }) => {
  const [reason, setReason] = useState("");
  const { mutate: closeTest, isPending } = useCloseLabOrderTest();

  // A fresh reason per test: the dialog is kept mounted and handed a different test, so
  // without this the next close would open with the last one's wording already in it.
  useEffect(() => {
    setReason("");
  }, [test?.id]);

  const trimmedReason = reason.trim();
  const canSubmit = trimmedReason.length > 0 && !isPending;

  const handleSubmit = () => {
    if (!canSubmit) return;

    closeTest(
      { testId: test.id, reason: trimmedReason },
      {
        onSuccess: () => {
          toast.success(`${getLabTestLabel(test)} closed.`);
          onClose();
        },
        onError: (error) => {
          // Not a failure to report: the test was resulted or closed while this dialog
          // was open. The hook has already invalidated the lists, so the row is on its
          // way out — all that is left is to say why it went.
          if (isAlreadySettledError(error)) {
            toast(
              "That test has already come back or been closed. The list has been refreshed."
            );
            onClose();

            return;
          }

          toast.error("We couldn't close this test. Please try again.");
        },
      }
    );
  };

  return (
    <Dialog
      open={!!test}
      onClose={onClose}
      title="This test won't come back"
      width={520}
    >
      <div className={s.body}>
        <p className={s.intro}>
          <strong>{getLabTestLabel(test)}</strong>
          {!!test?.patientName && ` for ${test.patientName}`} will be taken off the
          outstanding list. Say why — the sample was rejected, the test was
          cancelled, or it was sent elsewhere. This can&apos;t be undone.
        </p>

        <label className={s.label} htmlFor="close-lab-test-reason">
          Reason
        </label>
        <Textarea
          id="close-lab-test-reason"
          rows={4}
          value={reason}
          maxLength={REASON_MAX_LENGTH}
          onChange={(event) => setReason(event.target.value)}
          placeholder="e.g. Sample haemolysed; repeat ordered"
          disabled={isPending}
        />

        <div className={s.actions}>
          <Button variant="outlineGray" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {isPending ? "Closing..." : "Close test"}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};
